import * as React from 'react';
import { useParams } from 'react-router';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Table, TableBody, TableCell, TableFooter, TableHead, TableRow, TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useFetchTeamsQuery, useUpdateTeamPaymentMutation } from '../../store/team/TeamApi';
import { myConstants } from '../../shares/Constants';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import PaymentIcon from '@mui/icons-material/PaymentOutlined';
import { useSelector } from 'react-redux';


export default function TeamPaymentAdmin() 
{
  const { year } = useParams();
  const { data: teams, isLoading } = useFetchTeamsQuery({ year, clubLabel: '' });
  const [updatePayment] = useUpdateTeamPaymentMutation();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedTeam, setSelectedTeam] = React.useState(null);
  const [payDate, setPayDate] = React.useState(null);
  const [paySum, setPaySum] = React.useState('');

  /*
  * Behandler für das Aufrufen der Rechnungen...
  * Wir tuen dabei so, als ob wir einen Anchor erstellen
  * und diesen dann auch gleich drücken.
  */
  const token = useSelector(state => state.auth.token);

  const handleInvoice = (path) =>
  {

    fetch(
      myConstants.baseURL 
      + '/'
      + myConstants.endpoints.api.base
      + myConstants.endpoints.api.invoice
      + path, 
      {
        headers: { 'Authorization': `token ${token}` }
      }
    )
    .then( result => result.blob() )
    .then( blob => 
      {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${year}_invoices.zip`;
        anchor.click();
        URL.revokeObjectURL(url);    
      }
    )
  };



  /*
   * Warten, bis wir alles geladen haben
   */
  if (isLoading)
  {
    return <CircularProgress />;
  } 

  /*
    * Auf die Teams filter, die hier auch wirklich
    * relevant sind (keine zurückgezogenen oder auf der Warteliste stehenden) 
  */
  const registeredTeams = 
  (teams ?? []).filter(
    team => (team.status ?? myConstants.teamModel.status.registered) === myConstants.teamModel.status.registered
  );


  /*
   * Gruppieren der Teams nach den Clubs
  */ 
  const byClub = (registeredTeams ?? []).reduce((acc, team) => {
    const key = team.clubLabel;
    if (!acc[key]) acc[key] = [];
    acc[key].push(team);
    return acc;
  }, {});



  // Die Gesamtsumme berechnet sich jetzt nicht mehr über die clubTeams, sondern
  // über alle teams
  const grandTotalPlayers = (registeredTeams ?? [] ).reduce((sum, team) => sum + (team.players ?? 0), 0);
  const grandTotalFellows = (registeredTeams ?? [] ).reduce((sum, team) => sum + (team.fellows ?? 0), 0);
  const grandTotalBarSets = (registeredTeams ?? [] ).reduce((sum, team) => sum + (team.barSets ?? 0), 0);
  const grandTotalFee     = (registeredTeams ?? [] ).reduce((sum, team) => sum + (team.vacationFee ?? 0), 0);
  const grandTotalPaid    = (registeredTeams ?? [] ).reduce((sum, team) => sum + (team.feePayedSum ?? 0), 0);
  const grandTotalOpen    = grandTotalFee - grandTotalPaid;
  

  /*
   * Bei einem Druck auf eine der Zeilen wird automatisch
   * dieser Dialog angezeigt. 
   */
  const handleOpen = (team) => 
  {
    const currentMonth = dayjs().month() + 1
    
    setSelectedTeam(team);
    setPayDate(
        team.feePayedDate && dayjs(team.feePayedDate) > 1
      ? dayjs(team.feePayedDate) 
      : dayjs(`${year}-${currentMonth}-01`)
    );
    setPaySum(
        (team.feePayedSum ?? 0) > 0
      ? team.feePayedSum.toFixed(2)
      : team.vacationFee.toFixed(2)
    );
    setDialogOpen(true);
  };

  const handleSave = async () => {
    await updatePayment({
      year,
      clubLabel: selectedTeam.clubLabel,
      teamLabel: selectedTeam.label,
      teamCounter: selectedTeam.counter,
      feePayedDate: payDate?.format('YYYY-MM-DD'),
      feePayedSum: parseFloat(paySum),
    }).unwrap();
    setDialogOpen(false);
  };

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Verein</TableCell>
            <TableCell>Team</TableCell>
            <TableCell align="right">Spieler</TableCell>
            <TableCell align="right">Begleiter</TableCell>
            <TableCell>Nacht</TableCell>
            <TableCell align="right">Bierbänke</TableCell>            
            <TableCell align="right">Fälliger Beitrag</TableCell>
            <TableCell align="right">Bezahlt am</TableCell>
            <TableCell align="right">Bezahlt (€)</TableCell>
            <TableCell align="right">Offen (€)</TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {
            Object.entries(byClub).map(
              ([clubLabel, clubTeams]) => 
              {
                // clubTeams enthält die pro Verein sortierte Teams
                const totalPlayers  = clubTeams.reduce((sum, team) => sum + (team.players ?? 0), 0);
                const totalFellows  = clubTeams.reduce((sum, team) => sum + (team.fellows ?? 0), 0);
                const totalBarSets  = clubTeams.reduce((sum, team) => sum + (team.barSets ?? 0), 0);
                const totalFee      = clubTeams.reduce((sum, team) => sum + (team.vacationFee ?? 0), 0);
                const totalPaid     = clubTeams.reduce((sum, team) => sum + (team.feePayedSum ?? 0), 0);
                const totalOpen     = totalFee - totalPaid;

                return (
                  <React.Fragment key={clubLabel}>
                    {
                      clubTeams.map( 
                        team => 
                        (
                          <TableRow
                            key={`${team.label}_${team.counter}`}
                            hover
                            sx={
                              { 
                                cursor: 'pointer',
                                backgroundColor: 
                                    (team.feePayedSum ?? 0) >= (team.vacationFee ?? 0)
                                  ? 'success.light'
                                  : (team.vacationFee ?? 0) === 0
                                    ? 'failure.light'
                                    : (team.vacationFee ?? 0) > 0 
                                      ? 'warning.light'
                                      : 'inherit',
                              }
                            }                            
                          >
                            <TableCell>{clubLabel}</TableCell>
                            <TableCell>{team.label} ({team.counter})</TableCell>
                            <TableCell align="right">{team.players}</TableCell>
                            <TableCell align="right">{team.fellows}</TableCell>
                            <TableCell>{ 
                                (team.vacationType === myConstants.saturday)
                              ? "TG"
                              : (team.vacationType === myConstants.weekend)
                                ? "WG"
                                : "ÜG"
                            }</TableCell>
                            <TableCell align="right">{team.barSets}</TableCell>
                            <TableCell align="right">{team.vacationFee?.toFixed(2)}</TableCell>
                            <TableCell align="right">{team.feePayedDate}</TableCell>
                            <TableCell align="right">{Number(team.feePayedSum).toFixed(2)}</TableCell>
                            <TableCell align="right">{((team.vacationFee ?? 0) - (team.feePayedSum ?? 0)).toFixed(2)}</TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => handleOpen(team)}>
                                <PaymentIcon fontSize="small" />
                              </IconButton>
                             </TableCell>
                             <TableCell > 
                              <IconButton size="small" onClick={() => handleInvoice(`${year}/${team.clubLabel}/${team.label}/${team.counter}`)}>
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )
                      )
                    }
                  {/* Summenzeile pro Verein */}
                  <TableRow sx={{ backgroundColor: 'action.hover', fontWeight: 'bold' }}>
                    <TableCell colSpan={2}><strong>{clubLabel} – Summe</strong></TableCell>
                    <TableCell align="right"><strong>{totalPlayers}</strong></TableCell>
                    <TableCell align="right"><strong>{totalFellows}</strong></TableCell>
                    <TableCell />
                    <TableCell align="right"><strong>{totalBarSets}</strong></TableCell>
                    <TableCell align="right"><strong>{totalFee.toFixed(2)}</strong></TableCell>
                    <TableCell />
                    <TableCell align="right"><strong>{totalPaid.toFixed(2)}</strong></TableCell>
                    <TableCell align="right"><strong>{totalOpen.toFixed(2)}</strong></TableCell>
                    <TableCell />
                    <TableCell>                      
                      <IconButton  size="small"  onClick={ () => handleInvoice( `${year}/${clubLabel}` ) } >                        
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                );
              }
            )
          }
        </TableBody>
        <TableFooter>
          <TableRow sx={{ backgroundColor: 'primary.light' }}>
            <TableCell colSpan={2}><strong>Gesamtsumme</strong></TableCell>            
            <TableCell align="right"><strong>{grandTotalPlayers}</strong></TableCell>
            <TableCell align="right"><strong>{grandTotalFellows}</strong></TableCell>
            <TableCell />
            <TableCell align="right"><strong>{grandTotalBarSets}</strong></TableCell>
            <TableCell align="right"><strong>{grandTotalFee.toFixed(2)}</strong></TableCell>
            <TableCell />
            <TableCell align="right"><strong>{grandTotalPaid.toFixed(2)}</strong></TableCell>
            <TableCell align="right"><strong>{grandTotalOpen.toFixed(2)}</strong></TableCell>
            <TableCell />
            <TableCell>
              <IconButton  size="small"  onClick={ () => handleInvoice( `${year}` ) } >
                <PrintIcon fontSize="small" />
              </IconButton>
            </TableCell>                              
          </TableRow>
        </TableFooter>
      </Table>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Zahlung erfassen – {selectedTeam?.label} ({selectedTeam?.counter})</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, mt:1, mb:2 }}>
          <DatePicker
            label="Bezahlt am"
            value={payDate}
            onChange={setPayDate}
            sx={{ mt:1 }}
          />
          <TextField
            label="Bezahlter Betrag (€)"
            type="number"
            value={paySum}
            onChange={(e) => setPaySum(e.target.value)}
            inputProps={{ step: '0.01' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
